import { useState, useEffect, useMemo } from 'react';
import { referenceDataService } from '@/services/referenceDataService';
import type { ReferenceDataItem } from '@/types/referenceData.types';
import type { AutocompleteOption } from '@/components/ui/Autocomplete';

export function useItineraryFormOptions() {
  const [ports, setPorts] = useState<ReferenceDataItem[]>([]);
  const [purposes, setPurposes] = useState<ReferenceDataItem[]>([]);
  const [viaPoints, setViaPoints] = useState<ReferenceDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch reference data on mount
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [portsData, purposesData, viaPointsData] = await Promise.all([
          referenceDataService.getPorts(),
          referenceDataService.getPurposes(),
          referenceDataService.getViaPoints(),
        ]);

        setPorts(portsData);
        setPurposes(purposesData);
        setViaPoints(viaPointsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch reference data');
      } finally {
        setLoading(false);
      }
    };

    fetchReferenceData();
  }, []);

  // Convert to autocomplete options
  const portOptions: AutocompleteOption[] = useMemo(
    () => ports.map((port) => ({ id: port.id.toString(), name: port.name })),
    [ports]
  );

  const purposeOptions: AutocompleteOption[] = useMemo(
    () => purposes.map((purpose) => ({ id: purpose.id.toString(), name: purpose.name })),
    [purposes]
  );

  const viaPointOptions: AutocompleteOption[] = useMemo(
    () => viaPoints.map((via) => ({ id: via.id.toString(), name: via.name })),
    [viaPoints]
  );

  // Search functions for autocomplete
  const searchPorts = async (query: string): Promise<AutocompleteOption[]> => {
    if (!query.trim()) return portOptions.slice(0, 10);

    const filtered = portOptions.filter((option) =>
      option.name.toLowerCase().includes(query.toLowerCase())
    );
    return filtered.slice(0, 10);
  };

  const searchViaPoints = async (query: string): Promise<AutocompleteOption[]> => {
    if (!query.trim()) return viaPointOptions.slice(0, 10);

    const filtered = viaPointOptions.filter((option) =>
      option.name.toLowerCase().includes(query.toLowerCase())
    );
    return filtered.slice(0, 10);
  };

  return {
    portOptions,
    purposeOptions,
    viaPointOptions,
    searchPorts,
    searchViaPoints,
    loading,
    error,
  };
}
